'use client'

/**
 * `<InPageNav>` — sticky in-page section navigator with a three-stage reveal:
 *
 *   `hidden`  – nav rect is below the viewport     (opacity 0)
 *   `ghost`   – nav is in viewport but not pinned  (opacity 0.1)
 *   `stuck`   – nav is pinned at the sticky offset (opacity 1, pills stagger in)
 *
 * Active section detection is midpoint-based (a section becomes active when
 * its top edge crosses the viewport midpoint). Click handlers temporarily mute
 * scroll detection so smooth-scroll animation cannot flicker the highlight.
 *
 * Styling defaults target a dark glass surface (matches imandrax's main-page
 * nav). Light-mode and any per-product look should be customised by either
 *   a) overriding the `--inpage-nav-bg` CSS variable, or
 *   b) passing `surfaceClassName` / `pillClassName` / `activePillClassName`.
 *
 * Both hooks are exported separately so other sticky chrome (sub-headers,
 * floating ToCs, …) can reuse the same phase / active-section machinery.
 */

import { useRef, type MouseEvent } from 'react'
import { motion, type Variants } from 'framer-motion'
import { Join } from '../../lib/utils'
import { useStickyPhase, type StickyPhase } from './use-sticky-phase'
import { useActiveSection } from './use-active-section'

export interface InPageNavSection {
  /** Element `id` to scroll to. */
  id: string
  /** Pill label. */
  title: string
}

export interface InPageNavProps {
  sections: ReadonlyArray<InPageNavSection>
  /** Optional "back to top" pill rendered after the section pills. */
  topPillLabel?: string
  /** ARIA label for the wrapping `<nav>`. Default: `"On this page"`. */
  ariaLabel?: string
  /** Pixel offset from viewport top for the `stuck` phase. Default: `80`. */
  stickyTopPx?: number
  /**
   * Tailwind classes controlling positioning / breakpoint visibility of the
   * outer `<nav>`. Default: `'sticky top-24 z-40 hidden w-full sm:top-20 lg:block'`.
   */
  className?: string
  /** Override the glass shell wrapping the pill list. */
  surfaceClassName?: string
  /** Override the inactive pill class. */
  pillClassName?: string
  /** Override the active pill class. */
  activePillClassName?: string
  /** Per-phase opacity. Defaults: `{ hidden: 0, ghost: 0.1, stuck: 1 }`. */
  phaseOpacity?: Partial<Record<StickyPhase, number>>
}

const DEFAULT_PHASE_OPACITY: Record<StickyPhase, number> = {
  hidden: 0,
  ghost: 0.1,
  stuck: 1,
}

const listVariants: Variants = {
  hidden: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
  ghost: { transition: {} },
  stuck: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
      when: 'beforeChildren',
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: -4, transition: { duration: 0.2 } },
  ghost: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
  stuck: { opacity: 1, y: 0, transition: { duration: 0.32, ease: 'easeOut' } },
}

const DEFAULT_NAV_CLASS = 'sticky top-24 z-40 hidden w-full sm:top-20 lg:block'

const DEFAULT_SURFACE_CLASS =
  'flex items-center gap-0.5 rounded-2xl bg-(--inpage-nav-bg) px-1 py-1 backdrop-blur-md'

const DEFAULT_PILL_CLASS = Join(
  'flex items-center justify-center rounded-xl whitespace-nowrap',
  'px-3 py-1.5 sm:px-3.5 sm:py-2',
  'text-center text-[11px] font-medium tracking-wide sm:text-xs',
  'transition-colors duration-150',
  'focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:outline-none',
  'text-slate-300/70 hover:bg-white/10 hover:text-white',
)

const DEFAULT_ACTIVE_PILL_CLASS = 'bg-white/10 text-white'

export interface InPageNavPillProps {
  href: string
  active: boolean
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  pillClassName: string
  activePillClassName: string
  children: React.ReactNode
}

export function InPageNavPill({
  href,
  active,
  onClick,
  pillClassName,
  activePillClassName,
  children,
}: InPageNavPillProps) {
  return (
    <motion.li className="flex" variants={itemVariants}>
      <a
        href={href}
        onClick={onClick}
        className={Join(pillClassName, active && activePillClassName)}
      >
        {children}
      </a>
    </motion.li>
  )
}

export function InPageNav({
  sections,
  topPillLabel,
  ariaLabel = 'On this page',
  stickyTopPx = 80,
  className,
  surfaceClassName,
  pillClassName,
  activePillClassName,
  phaseOpacity,
}: InPageNavProps) {
  const navRef = useRef<HTMLElement>(null)
  const phase = useStickyPhase(navRef, { stickyTopPx })
  const sectionIds = sections.map((s) => s.id)
  const { activeIndex, notifyClick } = useActiveSection(sectionIds)

  const opacity = { ...DEFAULT_PHASE_OPACITY, ...phaseOpacity }[phase]
  const navCls = className ?? DEFAULT_NAV_CLASS
  const surfaceCls = surfaceClassName ?? DEFAULT_SURFACE_CLASS
  const pillCls = pillClassName ?? DEFAULT_PILL_CLASS
  const activeCls = activePillClassName ?? DEFAULT_ACTIVE_PILL_CLASS

  const handleTopClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    notifyClick()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <motion.nav
      ref={navRef}
      aria-label={ariaLabel}
      className={navCls}
      animate={{ opacity }}
      transition={{ duration: 0.35 }}
    >
      <div className="max-w-8xl mx-auto flex justify-center px-3 sm:px-4 lg:px-6">
        <motion.ul
          className={surfaceCls}
          variants={listVariants}
          initial="hidden"
          animate={phase}
        >
          {sections.map((section, i) => (
            <InPageNavPill
              key={section.id}
              href={`#${section.id}`}
              active={i === activeIndex}
              onClick={() => notifyClick()}
              pillClassName={pillCls}
              activePillClassName={activeCls}
            >
              {section.title}
            </InPageNavPill>
          ))}
          {topPillLabel ? (
            <InPageNavPill
              href="#"
              active={false}
              onClick={handleTopClick}
              pillClassName={pillCls}
              activePillClassName={activeCls}
            >
              {topPillLabel}
            </InPageNavPill>
          ) : null}
        </motion.ul>
      </div>
    </motion.nav>
  )
}
